import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { InputMultiselectComponent } from '../../../shared/input-multiselect/input-multiselect.component';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  PermissionService,
  REGION_ADMIN,
  S2S_ADMIN,
} from '../../../permissions.service';
import { HeroReport } from '../../../model/hero-report/hero-report.model';
import { CLASSIFICATION_OPTIONS } from '../../../../../ordnances';
import { Organization } from '../../../model/organization/organization.model';

interface FilterOptions {
  organizations?: {
    label: string;
    items: { label: string; value: string }[];
  }[];
  accounts?: { label: string; items: { label: string; value: string }[] }[];
  identifications?: {
    label: string;
    items: { label: string; value: string }[];
  }[];
  statuses?: { label: string; value: string }[];
}

@Component({
  selector: 'app-event-filters',
  standalone: true,
  imports: [ReactiveFormsModule, InputMultiselectComponent],
  templateUrl: './event-filters.component.html',
  styleUrl: './event-filters.component.scss',
})
export class EventFiltersComponent implements OnInit {
  @Input() organizations: Organization[] = [];
  @Input() regions = [];
  @Input() events: HeroReport[] = [];

  @Output() filtersChanged = new EventEmitter();

  filterOptions: FilterOptions = {
    statuses: [
      {
        label: $localize`:@@status.draft:Draft`,
        value: 'DRAFT'
      },
      {
        label: $localize`:@@status.pending:Pending`,
        value: 'PENDING'
      },
      {
        label: $localize`:@@status.completed:Completed`,
        value: 'COMPLETED'
      }
    ]
  };

  filtersForm!: FormGroup;
  showOrganizationFilter = false;

  constructor(
    private formBuilder: FormBuilder,
    private permissionService: PermissionService,
  ) { }

  ngOnInit(): void {
    this.showOrganizationFilter =
      this.permissionService.hasRole(S2S_ADMIN) ||
      this.permissionService.hasRole(REGION_ADMIN);

    this.setupForm();
    this.setupFilterOptions();
  }

  private setupForm() {
    this.filtersForm = this.formBuilder.group({
      ...((this.showOrganizationFilter && { organizationIds: [null] }) || {}),
      reportedByIds: [null],
      possibleIdentifications: [null],
      statuses: [null],
    });

    this.filtersForm.valueChanges.subscribe((filters) => {
      this.filtersChanged.emit(filters);
    });
  }

  private setupFilterOptions() {
    const groupedOrgs = this.organizations.reduce(
      (acc: Record<string, any>, org: Organization) => {
        const regionId = org.regionId || 'no-region';
        const region = this.regions.find((r: any) => r.id === regionId);
        const regionName = region?.name || $localize`:@@filter.noRegion:No Region`;

        if (!acc[regionId]) {
          acc[regionId] = {
            label: regionName,
            items: [],
          };
        }

        acc[regionId].items.push({
          label: org.name,
          value: org.id,
        });

        return acc;
      },
      {},
    );

    this.filterOptions.organizations = Object.values(groupedOrgs).map(
      (group: any) => ({
        label: group.label,
        items: group.items,
      }),
    );

    const groupedAccounts = this.events.reduce(
      (acc: Record<string, any>, report: HeroReport) => {
        const account = report.account;
        const orgId = account.organization?.id || 'no-org';
        const orgName = account.organization?.name || $localize`:@@filter.noOrganization:No Organization`;

        if (!acc[orgId]) {
          acc[orgId] = {
            label: orgName,
            items: new Map(),
          };
        }

        if (!acc[orgId].items.has(account.id)) {
          acc[orgId].items.set(account.id, {
            label: `${account.firstName} ${account.lastName}`,
            value: account.id,
          });
        }

        return acc;
      },
      {},
    );

    this.filterOptions.accounts = Object.values(groupedAccounts).map(
      (group: any) => ({
        label: group.label,
        items: Array.from(group.items.values()),
      }),
    );

    const groupedIdentifications = this.events.reduce(
      (
        acc: Record<string, Map<string, { label: string; value: string }>>,
        report: HeroReport,
      ) => {
        report.possibleIdentification.forEach((identification: string) => {
          const matched = CLASSIFICATION_OPTIONS.find(
            (ord: any) => ord.key === identification,
          );

          if (!matched) {
            return acc;
          }

          const group = matched.label;

          if (!acc[group]) {
            acc[group] = new Map();
          }

          if (!acc[group].has(identification)) {
            acc[group].set(identification, {
              label: matched
                ? `${matched.label} ${matched.size}`
                : identification,
              value: identification,
            });
          }

          return acc;
        });

        return acc;
      },
      {},
    );

    this.filterOptions.identifications = Object.keys(
      groupedIdentifications,
    ).map((group) => ({
      label: group,
      items: Array.from(groupedIdentifications[group].values()),
    }));
  }
}
